'use client';

import * as React from 'react';
import Image from 'next/image';
import { TbListNumbers, TbSitemap, TbUsersGroup } from 'react-icons/tb';

import { routes } from '@workspace/routes';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

import { CalendlyButton } from '~/components/calendly-button';

// The pain and the three ways Lumnis helps (Abu, Nov 1). Tabbed structure
// borrowed from Juicebox's features section, kept in this page's centered
// header and dot texture. Way in and open deals get drawn examples since the
// idea is simpler than the product screen; top accounts uses the real
// monitor screenshot (Abu).
const PIPELINE_WARM = {
  // One line that says what they get, not the pain (Abu, Nov 2).
  headline: 'Book meetings at your top accounts while you close the deals you have.',
  // Titles say the action and the pain, in 30mpc's plain framing (Abu).
  // Accounts first, then intros: the headline's order, in market then the
  // way in, and the order an AE works in (Abu, Nov 1).
  points: [
    {
      id: 'top-accounts',
      tab: 'Prioritized accounts',
      short: 'Priorities',
      icon: TbListNumbers,
      title: 'Know which accounts to work this week (and why now)',
      // Tab quotes reuse the customer quotes on the page (Abu, Nov 2): the
      // Native AE here, the Justin call on intros, the personalization quote
      // on research. Not extended past what was said.
      quote: {
        text: 'It bubbled up our champion… it found the very person that bought us.',
        source: 'AE at a Series A security company'
      },
      bullets: [
        {
          lead: 'See which accounts are high-intent.',
          text: 'Ranked by what changed this week, with the reason.'
        },
        {
          lead: 'See why now.',
          text: 'Hiring, new leaders, funding and what their people post.'
        },
        {
          lead: 'Get the next step.',
          text: 'Each account comes with who to contact and what to say.'
        }
      ],
      cta: 'Prioritize my accounts'
    },
    {
      id: 'way-in',
      tab: 'Warm intros',
      short: 'Intros',
      icon: TbUsersGroup,
      // "Referrals" is the word buyers use for customer intros; the tab keeps
      // "Warm intros" as the wider frame (Justin call, Abu, Nov 2).
      title: 'Get referrals and warm intros into the accounts that ignore cold email',
      quote: {
        text: 'We have a ton of success with referrals, but it’s hard to scale.',
        source: 'AE at a Series D company'
      },
      bullets: [
        {
          lead: 'See who knows them.',
          text: 'Your customers, teammates, past coworkers and investors who already know the buyer.'
        },
        {
          lead: 'Get the ask written.',
          text: 'Lumnis drafts the intro request for the person who knows them.'
        },
        {
          lead: 'Run referrals at scale.',
          text: 'Pick the customers you’re close to and see which of your target accounts each one knows.'
        },
        {
          lead: 'Bring in the next buying team.',
          text: 'Your champion can introduce you to the other team, and Lumnis drafts the ask.'
        }
      ],
      cta: 'Get an intro'
    },
    // Research is the bigger frame; watching open deals is its last step
    // (Abu, Nov 1). "Before it stalls" keeps the deal pain in the copy.
    {
      id: 'research',
      tab: 'Account research',
      short: 'Research',
      icon: TbSitemap,
      title: 'Know who decides on every deal (before it stalls)',
      quote: {
        text: 'The trustworthiness of the research enables us to do serious personalization.',
        source: 'AE at a seed-stage AI company'
      },
      bullets: [
        {
          lead: 'Map the org.',
          text: 'Who sits where and who makes the call.'
        },
        {
          lead: 'Know the people.',
          text: 'What each person posts about, cares about and engages with most.'
        },
        {
          lead: 'See competitor engagement.',
          text: 'Who at the account is liking and commenting on your competitor’s posts.'
        },
        {
          lead: 'Keep watching your open deals.',
          text: 'When something changes on a live deal, you hear about it before it stalls.'
        }
      ],
      cta: 'Research an account'
    }
  ]
} as const;

type PointId = (typeof PIPELINE_WARM.points)[number]['id'];

const CARD =
  'rounded-xl bg-[color:var(--paper)] shadow-[0_10px_30px_-18px_rgba(10,30,24,0.45)]';

// Example people and companies in the drawn visuals are fictional.
// A relationship web (Abu, Nov 2): you on the left, the buyer on the right,
// and the one person who connects you lit as the way in. Other paths were
// cut so the You, Sam, Maya chain reads at a glance; the caption says why. Positions are percentages of the frame,
// so the same web scales from desktop down to a phone.
type WebNode = {
  id: string;
  x: number;
  y: number;
  kind: 'you' | 'link' | 'buyer' | 'dot';
  initials?: string;
  name?: string;
  role?: string;
  org?: string;
  // Label above the circle, so the lit path's pill has room below it.
  labelAbove?: boolean;
};

const WEB_NODES: ReadonlyArray<WebNode> = [
  { id: 'you', x: 12, y: 62, kind: 'you', initials: 'You' },
  { id: 'sam', x: 46, y: 28, kind: 'link', initials: 'SK', name: 'Sam', role: 'Your customer', labelAbove: true },
  { id: 'maya', x: 84, y: 62, kind: 'buyer', initials: 'MC', name: 'Maya Chen', role: 'VP Security', org: 'Northwind Health' },
  // Background people: the rest of both networks, drawn as small dots.
  { id: 'a', x: 26, y: 88, kind: 'dot' },
  { id: 'b', x: 20, y: 30, kind: 'dot' },
  { id: 'c', x: 38, y: 74, kind: 'dot' },
  { id: 'd', x: 60, y: 8, kind: 'dot' },
  { id: 'e', x: 58, y: 90, kind: 'dot' },
  { id: 'g', x: 76, y: 20, kind: 'dot' },
  { id: 'h', x: 96, y: 34, kind: 'dot' },
  { id: 'i', x: 95, y: 97, kind: 'dot' },
  { id: 'j', x: 76, y: 92, kind: 'dot' },
  // Middle of the web, so the space under the lit path still reads as a
  // network after Priya and Alex were cut (Abu, Nov 2).
  { id: 'k', x: 34, y: 50, kind: 'dot' },
  { id: 'l', x: 50, y: 58, kind: 'dot' },
  { id: 'm', x: 64, y: 72, kind: 'dot' },
  { id: 'n', x: 46, y: 80, kind: 'dot' }
];

// No faint edge runs through a name label (polish pass, Abu, Nov 2).
const WEB_EDGES: ReadonlyArray<[string, string, 'best' | 'faint']> = [
  ['you', 'sam', 'best'],
  ['sam', 'maya', 'best'],
  ['you', 'a', 'faint'],
  ['you', 'b', 'faint'],
  ['you', 'c', 'faint'],
  ['b', 'sam', 'faint'],
  ['a', 'c', 'faint'],
  ['b', 'd', 'faint'],
  ['sam', 'g', 'faint'],
  ['d', 'g', 'faint'],
  ['g', 'maya', 'faint'],
  ['maya', 'h', 'faint'],
  ['g', 'h', 'faint'],
  ['i', 'j', 'faint'],
  ['m', 'j', 'faint'],
  ['e', 'j', 'faint'],
  ['you', 'k', 'faint'],
  ['k', 'l', 'faint'],
  ['c', 'l', 'faint'],
  ['l', 'm', 'faint'],
  ['m', 'maya', 'faint'],
  ['c', 'n', 'faint'],
  ['n', 'm', 'faint'],
  ['n', 'e', 'faint'],
  ['l', 'b', 'faint']
];

function WayInVisual(): React.JSX.Element {
  const byId = new Map(WEB_NODES.map((node) => [node.id, node]));
  return (
    <div className={cn(CARD, 'p-3 sm:p-5')}>
      <div className="relative aspect-square w-full sm:aspect-[7/5]">
        <svg
          aria-hidden
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 size-full overflow-visible"
        >
          {/* Soft mint halo under the lit path so it reads first. */}
          {WEB_EDGES.filter(([, , kind]) => kind === 'best').map(([from, to]) => {
            const a = byId.get(from);
            const b = byId.get(to);
            if (!a || !b) return null;
            return (
              <line
                key={`halo-${from}-${to}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                stroke="var(--mint-2)"
                strokeWidth={7}
              />
            );
          })}
          {WEB_EDGES.map(([from, to, kind]) => {
            const a = byId.get(from);
            const b = byId.get(to);
            if (!a || !b) return null;
            return (
              <line
                key={`${from}-${to}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                stroke={
                  kind === 'faint' ? 'var(--hairline)' : 'var(--accent-darker)'
                }
                strokeWidth={kind === 'best' ? 2.5 : 1}
              />
            );
          })}
        </svg>

        {/* The one fact that makes the lit path the best one, centered on the
            Sam to Maya line. Phones get the shorter wording. */}
        <span
          className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-[color:var(--accent-darker)] px-2 py-0.5 text-[10px] font-medium text-[color:var(--paper)] ring-4 ring-[color:var(--paper)] sm:text-[11px]"
          style={{ left: '65%', top: '45%' }}
        >
          <span className="sm:hidden">4 yrs at Brightpath</span>
          <span className="hidden sm:inline">4 yrs together at Brightpath</span>
        </span>

        {WEB_NODES.map((node) => (
          <div
            key={node.id}
            className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
            style={{ left: `${node.x}%`, top: `${node.y}%` }}
          >
            {node.kind === 'dot' ? (
              // Solid fill so the lines behind don't show through the dot.
              <span className="size-2 rounded-full border border-[color:color-mix(in_srgb,var(--ink)_22%,transparent)] bg-[color:var(--paper)] ring-[3px] ring-[color:var(--paper)]" />
            ) : (
              <span
                className={cn(
                  'flex items-center justify-center rounded-full font-semibold ring-4 ring-[color:var(--paper)]',
                  node.kind === 'you' &&
                    'size-10 bg-[color:var(--ink)] text-[11px] text-[color:var(--paper)] sm:size-11',
                  node.kind === 'link' &&
                    'size-9 border-2 border-[color:var(--accent-darker)] bg-[color:var(--mint)] text-[12px] text-[color:var(--accent-deep)] sm:size-10',
                  node.kind === 'buyer' &&
                    'size-12 border-2 border-[color:var(--accent-darker)] bg-[color:var(--mint)] text-[14px] text-[color:var(--accent-deep)] sm:size-14'
                )}
              >
                {node.initials}
              </span>
            )}
            {node.name && (
              <span
                className={cn(
                  'pointer-events-none absolute flex flex-col items-center whitespace-nowrap text-center leading-tight',
                  node.labelAbove ? 'bottom-full mb-1' : 'top-full mt-1'
                )}
              >
                <span className="text-[11px] font-semibold text-[color:var(--ink)] sm:text-[12px]">
                  {node.name}
                </span>
                <span className="text-[10px] text-[color:var(--ink-4)] sm:text-[11px]">
                  {node.role}
                </span>
                {node.org && (
                  <span className="text-[10px] text-[color:var(--ink-4)] sm:text-[11px]">
                    {node.org}
                  </span>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="mt-3 border-t border-[color:var(--hairline)] pt-3 text-center text-[12px] leading-snug text-[color:var(--ink-3)] [text-wrap:balance] sm:text-[13px]">
        Lumnis found that Sam, one of your customers, worked with Maya at
        Brightpath for 4 years.
      </p>
    </div>
  );
}

function TopAccountsVisual(): React.JSX.Element {
  return (
    <div className={cn(CARD, 'overflow-hidden')}>
      <div className="relative aspect-[8/5]">
        <Image
          src="/assets/how-it-works/prioritization.jpg"
          alt="A monitor ranking accounts, with Pivot marked Act now and the reason behind it"
          fill
          sizes="(min-width: 768px) 760px, 100vw"
          className="object-cover"
        />
      </div>
    </div>
  );
}

function ResearchVisual(): React.JSX.Element {
  // Each person gets a role chip and one short line, so the three cards stay
  // the same height and read at a glance (polish pass, Abu, Nov 2).
  const people = [
    {
      initials: 'MC',
      name: 'Maya Chen',
      role: 'VP of Security',
      chip: 'Decides',
      note: 'Helping on your deal',
      tone: 'good'
    },
    {
      initials: 'DP',
      name: 'Dev Patel',
      role: 'CFO',
      chip: 'Signs off',
      note: 'Hasn’t engaged yet',
      tone: 'muted'
    },
    {
      initials: 'LO',
      name: 'Lena Ortiz',
      role: 'Head of IT',
      chip: 'Watch',
      note: 'Liked a competitor’s post',
      tone: 'warn'
    }
  ] as const;
  const line =
    'border-[color:color-mix(in_srgb,var(--accent-darker)_30%,transparent)]';
  return (
    <div className={cn(CARD, 'p-4 sm:p-6')}>
      <p className="text-[14px] font-semibold text-[color:var(--ink)] sm:text-[15px]">
        Northwind Health
        <span aria-hidden className="mx-1.5 text-[color:var(--ink-4)]">·</span>
        Buying committee
      </p>

      {/* Org tree: the CEO, then the three people around the deal. */}
      <div className="mt-5 flex flex-col items-center">
        <div className="flex items-center gap-2.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--paper)] py-1.5 pl-1.5 pr-4">
          <span className="flex size-8 items-center justify-center rounded-full bg-[color:var(--ink)] text-[11px] font-semibold text-[color:var(--paper)]">
            JW
          </span>
          <span className="text-left leading-tight">
            <span className="block text-[13px] font-semibold text-[color:var(--ink)]">
              Jordan Wells
            </span>
            <span className="block text-[11px] text-[color:var(--ink-4)]">CEO</span>
          </span>
        </div>
        <span className={cn('h-5 border-l', line)} />
        <div className="relative grid w-full grid-cols-3 gap-2 pt-5 sm:gap-3">
          {/* Crossbar from the first to the last report, rounded at the ends. */}
          <span
            className={cn(
              'absolute left-[16.67%] right-[16.67%] top-0 h-5 rounded-t-lg border-x border-t',
              line
            )}
          />
          <span
            className={cn('absolute left-1/2 top-0 h-5 border-l', line)}
          />
          {people.map((person) => (
            <div
              key={person.name}
              className={cn(
                'flex flex-col items-center rounded-xl border px-2 pb-3 pt-3.5 text-center sm:px-3',
                person.tone === 'good'
                  ? 'border-[color:var(--accent-darker)] bg-[color:var(--mint)] shadow-[0_6px_18px_-12px_rgba(0,62,47,0.5)]'
                  : 'border-[color:var(--hairline)] bg-[color:var(--paper)]'
              )}
            >
              <span
                className={cn(
                  'flex size-9 items-center justify-center rounded-full text-[11px] font-semibold sm:size-10 sm:text-[12px]',
                  person.tone === 'good'
                    ? 'border-2 border-[color:var(--accent-darker)] bg-[color:var(--paper)] text-[color:var(--accent-deep)]'
                    : 'border border-[color:var(--hairline)] bg-[color:var(--paper-2,var(--paper))] text-[color:var(--ink-3)]'
                )}
              >
                {person.initials}
              </span>
              <p className="mt-2 text-[13px] font-semibold leading-tight text-[color:var(--ink)] sm:text-[14px]">
                {person.name}
              </p>
              <p className="mt-0.5 text-[11px] leading-tight text-[color:var(--ink-4)] sm:text-[12px]">
                {person.role}
              </p>
              <span
                className={cn(
                  'mt-2.5 whitespace-nowrap rounded-full px-1.5 py-0.5 font-mono text-[9px] font-medium uppercase tracking-[0.04em] sm:px-2 sm:text-[10px] sm:tracking-[0.08em]',
                  person.tone === 'good' &&
                    'bg-[color:var(--accent-darker)] text-[color:var(--paper)]',
                  person.tone === 'muted' &&
                    'bg-[color:color-mix(in_srgb,var(--ink)_7%,transparent)] text-[color:var(--ink-3)]',
                  person.tone === 'warn' &&
                    'bg-[color:color-mix(in_srgb,#d9a441_20%,transparent)] text-[#93650f]'
                )}
              >
                {person.chip}
              </span>
              <p className="mt-2 text-[11px] leading-snug text-[color:var(--ink-3)] sm:text-[12px]">
                {person.note}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const VISUALS: Record<PointId, () => React.JSX.Element> = {
  'way-in': WayInVisual,
  'top-accounts': TopAccountsVisual,
  research: ResearchVisual
};

function PointText({
  point,
  titleId
}: {
  point: (typeof PIPELINE_WARM.points)[number];
  titleId?: string;
}): React.JSX.Element {
  return (
    <>
      <h3
        id={titleId}
        className="text-[26px] font-semibold leading-tight tracking-[-0.02em] text-[color:var(--ink)] md:text-[32px]"
      >
        {point.title}
      </h3>
      {'quote' in point && (
        <figure className="mt-5 max-w-md border-l-2 border-[color:var(--accent-deep)] pl-4">
          <blockquote className="text-[16px] italic leading-relaxed text-[color:var(--ink)]">
            “{point.quote.text}”
          </blockquote>
          <figcaption className="mt-1 text-[13px] text-[color:var(--ink-3)]">
            {point.quote.source}
          </figcaption>
        </figure>
      )}
      <ul className="mt-5 max-w-md space-y-3">
        {point.bullets.map((bullet) => (
          <li
            key={bullet.lead}
            className="flex gap-3 text-[16px] leading-relaxed text-[color:var(--ink-3)]"
          >
            <span
              aria-hidden
              className="mt-[11px] size-1.5 shrink-0 rounded-full bg-[color:var(--accent-darker)]"
            />
            <span>
              <span className="font-semibold text-[color:var(--ink)]">
                {bullet.lead}
              </span>{' '}
              {bullet.text}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          asChild
          className="marketing-cta"
        >
          <a href={routes.dashboard.auth.SignUp}>{point.cta}</a>
        </Button>
        <CalendlyButton
          text="Book a demo"
          variant="outline"
          className="h-11 rounded-md px-4 text-[13px]"
        />
      </div>
    </>
  );
}

export function PipelineWarmSection(): React.JSX.Element {
  const [active, setActive] = React.useState<PointId>('top-accounts');
  const sectionRef = React.useRef<HTMLElement>(null);

  // The tab and the pinned visual follow the last point whose top has
  // passed 40% of the screen. Tall phone panels (text over visual) would
  // otherwise keep the previous point active while you read the next one.
  // Only the visible layout's points count.
  React.useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const line = window.innerHeight * 0.4;
      let current: PointId | null = null;
      for (const element of section.querySelectorAll<HTMLElement>(
        '[data-point]'
      )) {
        if (element.offsetParent === null) continue;
        if (element.getBoundingClientRect().top <= line) {
          current = element.dataset.point as PointId;
        }
      }
      if (current) setActive(current);
    };
    const onScroll = (): void => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const scrollTo = (id: PointId): void => {
    const target = Array.from(
      sectionRef.current?.querySelectorAll<HTMLElement>(
        `[data-point="${id}"]`
      ) ?? []
    ).find((element) => element.offsetParent !== null);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <section
      ref={sectionRef}
      aria-labelledby="warm-title"
      className="w-full py-14 md:py-20"
    >
      <header className="mx-auto max-w-4xl px-4 text-center sm:px-6">
        <h2
          id="warm-title"
          className="display-h2 text-3xl text-[color:var(--ink)] [text-wrap:balance] md:text-4xl lg:text-5xl"
        >
          {PIPELINE_WARM.headline}
        </h2>
      </header>

      {/* One frame: the tabs are its top edge and stick under the site
          header while the points scroll by. */}
      {/* Sits inside the page frame; from 1024px its side borders land on
          the homepage frame lines. */}
      <div className="mx-4 mt-10 border border-[color:var(--hairline)] bg-[color:var(--paper)] [--warm-tabs-bottom:119px] sm:mx-6 lg:mx-[var(--landing-frame-inset,24px)]">
        <nav
          aria-label="Ways Lumnis helps"
          className="sticky top-[69px] z-20 flex border-b border-[color:var(--hairline)] bg-[color:var(--paper-2)]"
        >
          {PIPELINE_WARM.points.map((point) => {
            const isActive = point.id === active;
            return (
              <button
                key={point.id}
                type="button"
                aria-current={isActive ? 'true' : undefined}
                onClick={() => scrollTo(point.id)}
                className={cn(
                  '-mb-px flex-1 whitespace-nowrap border-b border-r px-3 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.08em] transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--accent-darker)] lg:flex-none lg:px-7 sm:text-[12px]',
                  'border-r-[color:var(--hairline)] max-lg:last-of-type:border-r-0',
                  isActive
                    ? 'border-b-[color:var(--paper)] bg-[color:var(--paper)] text-[color:var(--accent-darker)]'
                    : 'border-b-[color:var(--hairline)] text-[color:var(--ink-4)] hover:text-[color:var(--ink-2)]'
                )}
              >
                <span className="flex items-center justify-center gap-2">
                  <point.icon
                    aria-hidden
                    className="hidden size-4 shrink-0 sm:block"
                  />
                  {/* Short labels keep three full-width tabs on one line below desktop. */}
                  <span className="lg:hidden">{point.short}</span>
                  <span className="hidden lg:inline">{point.tab}</span>
                </span>
              </button>
            );
          })}
          <span
            aria-hidden
            className="hidden flex-1 lg:block"
          />
        </nav>

        {/* Desktop: the text scrolls on the left while the visual stays
            pinned on the right and swaps to the point in view. */}
        <div className="hidden md:grid md:grid-cols-2">
          <div>
            {PIPELINE_WARM.points.map((point) => (
              <article
                key={point.id}
                data-point={point.id}
                aria-labelledby={`warm-${point.id}-title`}
                className="flex min-h-[72vh] flex-col justify-center border-b border-[color:var(--hairline)] px-8 py-14 last:border-b-0 lg:px-14"
              >
                <PointText
                  point={point}
                  titleId={`warm-${point.id}-title`}
                />
              </article>
            ))}
          </div>
          {/* Only the pinned box is green, so no green scrolls up under
              the see-through site header. */}
          <div>
            <div
              aria-hidden
              className="lumnis-warm-panel sticky top-[var(--warm-tabs-bottom)] flex h-[calc(100vh-var(--warm-tabs-bottom))] max-h-[780px] items-center justify-center px-6 lg:px-10"
            >
              <div className="relative w-full max-w-[760px]">
                {PIPELINE_WARM.points.map((point, index) => {
                  const Visual = VISUALS[point.id];
                  const isActive = point.id === active;
                  return (
                    <div
                      key={point.id}
                      className={cn(
                        'transition-opacity duration-500 ease-out motion-reduce:transition-none',
                        index > 0 &&
                          'absolute inset-x-0 top-1/2 -translate-y-1/2',
                        isActive
                          ? 'opacity-100'
                          : 'pointer-events-none opacity-0'
                      )}
                    >
                      <Visual />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Phones: each point stacks its text over its visual, inside the
            same frame. */}
        <div className="md:hidden">
          {PIPELINE_WARM.points.map((point) => {
            const Visual = VISUALS[point.id];
            return (
              <article
                key={point.id}
                data-point={point.id}
                className="border-b border-[color:var(--hairline)] last:border-b-0"
              >
                <div className="px-5 py-8">
                  <PointText point={point} />
                </div>
                <div className="lumnis-warm-panel px-5 py-10">
                  <Visual />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
